import { Paper, Stack } from '@mui/material';
import {
  usePermissionsForCurrentUser,
  useSelectedOrgUnit,
} from '@retail/app/stores/selected-context';
import {
  AssortmentItemsActionToolbar,
  AssortmentPricesItemsTable,
  useAssortmentItemActions,
} from '@retail/my-assortment/components';
import { useIncludeVatMyAssortment } from '@retail/my-assortment/context';
import {
  AssortmentItemMinimal,
  AssortmentPrices,
  AssortmentView,
} from '@retail/my-assortment/types';
import { useSelectedSearchParamState } from '@shared/hooks';
import { useCallback, useMemo, useState } from 'react';
import { CalculusCardContainer } from '../CalculusCard';
import {
  BulkEditSalesPriceDialogContainer,
  DeleteItemsDialogContainer,
  EditAssortmentItemDialog,
  EditSalesPriceDialogContainer,
} from '../AssortmentItems';
import {
  BulkEditItemsDialogContainer,
  EditAssortmentTagsDialogContainer,
  MgItemContainer,
  MgItemDialogContainer,
} from '@retail/products/containers';
import { AssortmentCompetitorView } from './AssortmentCompetitorView';
import { SuspenseWithSentryErrorBoundary } from '@retail/components';
interface Props {
  items: AssortmentPrices[];
  loadingFetchItems: boolean;
  selectedAssortmentView: AssortmentView;
  emptyAssortment: boolean;
  toggleAddItemsDialog: () => void;
}

export function AssortmentPricesItemsContainer({
  items,
  loadingFetchItems,
  selectedAssortmentView,
  emptyAssortment,
  toggleAddItemsDialog,
}: Props) {
  const { includeVat } = useIncludeVatMyAssortment();
  const { assortmentId, appTenant, orgUnitType } = useSelectedOrgUnit();
  const [selectedCalculusCardItem, , setSelectedCalculusCardItemId] = useSelectedSearchParamState({
    options: items,
    searchParamKey: 'supplierItem',
    getOptionId: ({ mgSupplierItemId }) => mgSupplierItemId,
  });
  const [selectedProductCardItem, , setSelectedProductCardItemId] = useSelectedSearchParamState({
    options: items,
    searchParamKey: 'mgItem',
    getOptionId: ({ mgSupplierItemId }) => mgSupplierItemId,
  });

  const onSupplierItemChange = useCallback(
    (mgSupplierItemId: number | null) => setSelectedCalculusCardItemId(mgSupplierItemId ?? null),
    [setSelectedCalculusCardItemId]
  );

  const { userCanRead, userCanDelete } = usePermissionsForCurrentUser();
  const isDeleteEnabled = userCanDelete('ASSORTMENT_ITEMS');
  const isPriceSuggestionEnabled = userCanRead('PRICE_CALCULATION__PRICE_SUGGESTIONS');
  const isPriceCalculationEnabled = userCanRead('PRICE_CALCULATION');
  const {
    itemsChecked,
    itemsForDeletion,
    bulkEditPriorities,
    bulkEditPrices,
    bulkEditTags,
    toggleBulkEditPriorities,
    toggleBulkEditPrices,
    toggleBulkEditTags,
    setItemForDeletion,
    resetDeletedItems,
    resetCheckedItems,
  } = useAssortmentItemActions();

  const [itemToEdit, setItemToEdit] = useState<AssortmentPrices | undefined>(undefined);
  const [itemToEditPrices, setItemToEditPrices] = useState<AssortmentPrices | undefined>(undefined);

  const supplierIdByMgSupplierItemId = useMemo(
    () => new Map(items.map((item) => [item.mgSupplierItemId, item.supplier.id])),
    [items]
  );
  const getSupplierIdForCalculus = useCallback(
    (mgSupplierItemId: number) => supplierIdByMgSupplierItemId.get(mgSupplierItemId),
    [supplierIdByMgSupplierItemId]
  );

  return (
    <Paper sx={{ display: 'flex', flexGrow: 1, overflow: 'auto' }}>
      <Stack flexGrow={1} flexDirection="row" overflow="hidden">
        {selectedAssortmentView === 'COMPETITOR_PRICES' ? (
          <SuspenseWithSentryErrorBoundary>
            <AssortmentCompetitorView
              assortmentId={assortmentId}
              externalItemSource={appTenant.externalItemSource}
              viewCalculusCard={setSelectedCalculusCardItemId}
              viewProductCard={setSelectedProductCardItemId}
              getSupplierIdForCalculus={
                isPriceCalculationEnabled ? getSupplierIdForCalculus : undefined
              }
            />
          </SuspenseWithSentryErrorBoundary>
        ) : (
          <AssortmentPricesItemsTable
            items={items}
            emptyAssortment={emptyAssortment}
            selectedItem={selectedCalculusCardItem}
            loadingFetchItems={loadingFetchItems}
            includeVat={includeVat}
            selectedView={selectedAssortmentView}
            isDeleteEnabled={isDeleteEnabled}
            isPriceCalculationEnabled={isPriceCalculationEnabled}
            isPriceSuggestionEnabled={isPriceSuggestionEnabled}
            orgUnitType={orgUnitType}
            externalItemSource={appTenant.externalItemSource}
            showMainSupplierName={appTenant.hasMainSupplier}
            toggleAddItemsDialog={toggleAddItemsDialog}
            viewCalculusCard={setSelectedCalculusCardItemId}
            viewProductCard={setSelectedProductCardItemId}
            editItem={setItemToEdit}
            editPrices={isPriceCalculationEnabled ? setItemToEditPrices : undefined}
            deleteItem={isDeleteEnabled ? setItemForDeletion : undefined}
          />
        )}

        {selectedCalculusCardItem && (
          <CalculusCardContainer
            key={selectedCalculusCardItem.mgSupplierItemId}
            {...selectedCalculusCardItem}
            assortmentId={assortmentId}
            onSupplierItemChange={onSupplierItemChange}
          />
        )}
        {selectedProductCardItem && (
          <MgItemDialogContainer
            open
            onClose={() => setSelectedProductCardItemId(null)}
            itemNumber={selectedProductCardItem.mgItemNumber}
          >
            {({ mgItem }) => (
              <MgItemContainer mgItem={mgItem} showAddItem={false} hiddenBreadcrumbs />
            )}
          </MgItemDialogContainer>
        )}
        {!!itemsChecked.length && (
          <AssortmentItemsActionToolbar
            totalItems={items.length ?? 0}
            isUpdatePricesEnabled={isPriceCalculationEnabled}
          />
        )}
      </Stack>
      {!!itemsForDeletion.length && (
        <DeleteItemsDialogContainer
          assortmentId={assortmentId}
          itemsForDeletion={itemsForDeletion}
          resetCheckedItems={resetDeletedItems}
        />
      )}

      {bulkEditPrices && (
        <BulkEditSalesPriceDialogContainer
          assortmentItems={items.filter((item) => itemsChecked.includes(item.mgSupplierItemId))}
          includeVat={includeVat}
          onClose={() => {
            toggleBulkEditPrices(false);
            resetCheckedItems();
          }}
        />
      )}

      {bulkEditPriorities && (
        <BulkEditItemsDialogContainer
          selectedItems={
            items.filter((item) =>
              itemsChecked.includes(item.mgSupplierItemId)
            ) as AssortmentItemMinimal[]
          }
          onClose={() => toggleBulkEditPriorities(false)}
          resetCheckedItems={resetCheckedItems}
        />
      )}

      {bulkEditTags && (
        <EditAssortmentTagsDialogContainer
          assortmentId={assortmentId}
          selectedItems={
            items.filter((item) =>
              itemsChecked.includes(item.mgSupplierItemId)
            ) as AssortmentItemMinimal[]
          }
          onClose={() => toggleBulkEditTags(false)}
          resetCheckedItems={resetCheckedItems}
        />
      )}

      {itemToEdit && (
        <EditAssortmentItemDialog open item={itemToEdit} onClose={() => setItemToEdit(undefined)} />
      )}

      {itemToEditPrices && (
        <EditSalesPriceDialogContainer
          item={itemToEditPrices}
          includeVat={includeVat}
          onClose={() => setItemToEditPrices(undefined)}
        />
      )}
    </Paper>
  );
}
